import React from 'react';
import { useSelector } from 'react-redux';
import { defineCustomElements } from '@om-design-system/web-components/loader';
import { selectCurrentToken } from '../../../features/auth/authSlice.js';
import { useAuditLogs } from './hooks/useAuditLogs';
import AuditLogFilters from './components/AuditLogFilters';
import AuditLogTable from './components/AuditLogTable';
import ErrorDisplay from './components/ErrorDisplay';
import OmPagination from '../../../components/ui/OmPagination';
import BackButton from '../../../components/ui/BackButton';

/**
 * AuditLogs Main Container Component
 * 
 * Minimal container that orchestrates all audit log functionality using modular components.
 * Replaces the 400+ line monolithic component with a clean, maintainable structure.
 * All OM Design System components are integrated for consistency.
 * 
 * Maximum 60 lines - focused only on component assembly and prop passing.
 */
const AuditLogs = () => {
  // Initialize OM web components
  React.useEffect(() => {
    defineCustomElements();
  }, []);

  // Authentication state
  const token = useSelector(selectCurrentToken);

  // Custom hook provides all state management and API operations
  const {
    // State
    auditLogs,
    loading,
    error,
    exportLoading,
    pagination,
    filters,
    
    // Event handlers
    handleFilterChange,
    handleApplyFilters,
    handleClearFilters,
    handlePageChange,
    handleExportCSV
  } = useAuditLogs();

  // Authentication guard
  if (!token) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center items-center h-64">
          <p className="text-gray-500">Please log in to view audit logs.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Back Button */}
        <div className="mb-6">
          <BackButton />
        </div>

        {/* Page Header */}
        <div className="mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Audit Logs</h1>
              <p className="mt-1 text-sm text-gray-600">
                View and monitor all system activities and user actions.
              </p>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && <ErrorDisplay error={error} />}

        {/* Filters Section */}
        <AuditLogFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearFilters}
          onExportCSV={handleExportCSV}
          exportLoading={exportLoading}
        />

        {/* Audit Logs Table */}
        <AuditLogTable
          auditLogs={auditLogs}
          loading={loading}
          pagination={pagination}
          onPageChange={handlePageChange}
        />

        {/* Pagination Component */}
        {!loading && !error && pagination?.total > 0 && (
          <div className="mt-6">
            <OmPagination
              currentPage={pagination.page || 1}
              totalItems={pagination.total}
              itemsPerPage={pagination.limit || 20}
              onPageChange={handlePageChange}
              showResultsInfo={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogs;